import { Box, styled, Tooltip, Typography } from '@mui/material';
import type { Column } from 'react-table';
import { WorkerStatus } from 'api/services/finance/payments.schema';
import { ContextMenuButton } from 'redesign';
import { TFunctionNonStrict } from 'i18next';
import { PaymentUsersCellProp, PaymentUsersColumnDefinition } from './payment-users.types';

export const getPaymentUsersColumnDefinition: (
  t: TFunctionNonStrict<'hq-screen-specific', undefined>
) => Column<PaymentUsersColumnDefinition>[] = (t) => {
  const workerName: Column<PaymentUsersColumnDefinition> = {
    Header: () => (
      <Header>
        {t('payment_users.table_headers.flexworker_name', { ns: 'hq-screen-specific' })}
      </Header>
    ),
    accessor: 'fullName',
    Cell: WorkerCell,
  };

  const iban: Column<PaymentUsersColumnDefinition> = {
    Header: () => (
      <Header>{t('payment_users.table_headers.iban', { ns: 'hq-screen-specific' })}</Header>
    ),
    accessor: 'iban',
    Cell: IbanCell,
  };

  const status: Column<PaymentUsersColumnDefinition> = {
    Header: () => (
      <Header>{t('payment_users.table_headers.status', { ns: 'hq-screen-specific' })}</Header>
    ),
    accessor: 'workerStatus',
    width: '280px',
    Cell: StatusCell,
  };

  const actions: Column<PaymentUsersColumnDefinition> = {
    Header: () => (
      <Header>{t('payment_users.table_headers.actions', { ns: 'hq-screen-specific' })}</Header>
    ),
    accessor: 'actions',
    width: '100px',
    Cell: ActionCell,
  };

  return [workerName, iban, status, actions];
};

const Header = ({ children }: React.PropsWithChildren<unknown>) => (
  <Typography variant="body2" style={{ fontWeight: 'bold' }}>
    {children}
  </Typography>
);

const WorkerCell = (cell: PaymentUsersCellProp<'fullName'>) => {
  return (
    <Tooltip title={cell.value} placement="bottom-start">
      <Typography variant="body1" noWrap>
        {cell.value}
      </Typography>
    </Tooltip>
  );
};

const IbanCell = (cell: PaymentUsersCellProp<'iban'>) => {
  return (
    <Typography variant="body1" noWrap>
      {cell.value}
    </Typography>
  );
};

const StatusCell = (cell: PaymentUsersCellProp<'workerStatus'>) => {
  const { status, hasPendingBankAccount, isBlocked } = cell.value;
  const label = hasPendingBankAccount
    ? 'In afwachting van IBAN-goedkeuring'
    : isBlocked
      ? 'Geblokkeerd'
      : workerStatusTranslations[status];

  return (
    <StatusBox status={status} hasPendingBankAccount={hasPendingBankAccount} isBlocked={isBlocked}>
      <Typography variant="body2" noWrap>
        {label}
      </Typography>
    </StatusBox>
  );
};

const ActionCell = (cell: PaymentUsersCellProp<'actions'>) => {
  const actions = cell.getRowActions(cell.value.flexWorkerId, cell.value.isBlocked);
  return <ContextMenuButton>{actions}</ContextMenuButton>;
};

const StatusBox = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'status' && prop !== 'hasPendingBankAccount' && prop !== 'isBlocked',
})<{
  status: WorkerStatus;
  hasPendingBankAccount: boolean;
  isBlocked: boolean;
}>(({ status, hasPendingBankAccount, isBlocked, theme }) => {
  const backgroundMap: Record<WorkerStatus, string> = {
    [WorkerStatus.ONBOARDED]: theme.palette.success.light,
    [WorkerStatus.ONBOARDING]: theme.palette.grey[400],
    [WorkerStatus.NOT_ONBOARDING]: theme.palette.lwWarning[100],
  };

  const backgroundColor = isBlocked
    ? theme.palette.error.main
    : hasPendingBankAccount
      ? theme.palette.grey[200]
      : backgroundMap[status];

  return {
    display: 'inline-block',
    padding: `${theme.spacing(1)} ${theme.spacing(3)}`,
    borderRadius: theme.spacing(1),
    backgroundColor,
  };
});

const workerStatusTranslations: Record<WorkerStatus, string> = {
  [WorkerStatus.ONBOARDED]: 'Onboarding voltooid',
  [WorkerStatus.NOT_ONBOARDING]: 'Onboarding niet gestart',
  [WorkerStatus.ONBOARDING]: 'In onboardingproces',
};
